import React from "react";
import { Chip } from "@mui/material";
import { Email, Business, CheckCircle, Error, Warning, AddCircle } from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import PropTypes from "prop-types";

const StatisticsPanel = ({ stats, reportName = "RejectedStep" }) => {
  // Calcular porcentajes
  const validPercentage =
    stats.totalEmails > 0 ? Math.round((stats.totalValidEmails / stats.totalEmails) * 100) : 0;

  const activePercentage =
    stats.totalReports > 0 ? Math.round((stats.activeReports / stats.totalReports) * 100) : 0;

  return (
    <MDBox mt={3} p={2} bgcolor="background.paper" borderRadius="lg" boxShadow={1}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" color="text" fontWeight="medium">
          <Email sx={{ mr: 1, verticalAlign: "middle" }} />
          Resumen de Reportes
        </MDTypography>

        <Chip
          label={`Reporte: ${reportName}`}
          size="small"
          sx={{
            color: "#0288d1",
            borderColor: "#0288d1",
          }}
          variant="outlined"
        />
      </MDBox>

      {/* Primera fila: Estadísticas principales */}
      <MDBox display="flex" flexWrap="wrap" gap={3} mb={2}>
        {/* Total de Comercios */}
        <MDBox textAlign="center" flex="1" minWidth="120px">
          <MDTypography variant="button" color="secondary" display="block" gutterBottom>
            <Business sx={{ mr: 0.5, fontSize: "0.875rem", verticalAlign: "middle" }} />
            Comercios
          </MDTypography>
          <MDTypography variant="h4" color="info" fontWeight="bold">
            {stats.totalReports || 0}
          </MDTypography>
        </MDBox>

        {/* Correos Válidos */}
        <MDBox textAlign="center" flex="1" minWidth="120px">
          <MDTypography variant="button" color="secondary" display="block" gutterBottom>
            <CheckCircle
              sx={{ mr: 0.5, fontSize: "0.875rem", verticalAlign: "middle", color: "#2e7d32" }}
            />
            Válidos
          </MDTypography>
          <MDBox>
            <MDTypography variant="h4" sx={{ color: "#2e7d32" }} fontWeight="bold">
              {stats.totalValidEmails || 0}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              ({validPercentage}%)
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Correos sin Email */}
        <MDBox textAlign="center" flex="1" minWidth="120px">
          <MDTypography variant="button" color="secondary" display="block" gutterBottom>
            <Error
              sx={{ mr: 0.5, fontSize: "0.875rem", verticalAlign: "middle", color: "#d32f2f" }}
            />
            Sin Email
          </MDTypography>
          <MDBox>
            <MDTypography variant="h4" sx={{ color: "#d32f2f" }} fontWeight="bold">
              {stats.emailsWithoutEmail || 0}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              ({100 - validPercentage}%)
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Segunda fila: Estado */}
      <MDBox mb={3}>
        <MDTypography variant="button" color="secondary" display="block" gutterBottom>
          Estado del Reporte
        </MDTypography>
        <MDBox
          display="flex"
          alignItems="center"
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundColor:
              stats.activeReports > 0 ? "rgba(46, 125, 50, 0.08)" : "rgba(158, 158, 158, 0.08)",
          }}
        >
          <CheckCircle
            sx={{
              mr: 1.5,
              color: stats.activeReports > 0 ? "#2e7d32" : "#9e9e9e",
            }}
          />
          <MDTypography
            variant="body1"
            sx={{
              color: stats.activeReports > 0 ? "#2e7d32" : "#757575",
              fontWeight: stats.activeReports > 0 ? "medium" : "normal",
            }}
          >
            {stats.activeReports > 0
              ? `${stats.activeReports} comercios activos`
              : "Reporte inactivo"}
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Cuarta fila: Resumen general */}
      <MDBox p={1.5} borderRadius={1} sx={{ backgroundColor: "rgba(33, 150, 243, 0.04)" }}>
        <MDTypography variant="caption" color="text.secondary">
          <strong>Resumen:</strong> {stats.totalReports || 0} comercios configurados con{" "}
          {stats.totalEmails || 0} correos totales.
          {stats.totalValidEmails > 0
            ? ` ${stats.totalValidEmails} correos válidos configurados.`
            : " Sin correos válidos configurados."}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

StatisticsPanel.propTypes = {
  reportName: PropTypes.string,
  stats: PropTypes.shape({
    totalReports: PropTypes.number,
    activeReports: PropTypes.number,
    inactiveReports: PropTypes.number,
    totalEmails: PropTypes.number,
    totalValidEmails: PropTypes.number,
    emailsWithoutEmail: PropTypes.number,
  }).isRequired,
};

export default React.memo(StatisticsPanel);
